import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { CVD_TYPES, CvdInfo, CvdType, simulateCvd } from '../../util/cvd-image';

/** What the card shows, for the page around it. */
export interface CvdState {
  /** the picture, scaled to at most MAX_SIDE on its long side, as drawn */
  base: ImageData | null;
  type: CvdType;
  name: string;
}

/**
 * The Color Blindness Simulator card (2026-10-15): a picture (a random Codigrate theme's IDE screenshot to start,
 * or one the visitor uploads, drops or pastes) on the right, split in two with a handle to drag: the original on
 * the left of it, the picture as the chosen colour vision deficiency sees it on the right. The conditions and the
 * download on the left. Everything is drawn on canvases in the browser; nothing is uploaded.
 */
/** each group's mark: the two colors it confuses, as two overlapping dots */
const GROUP_DOTS: Record<string, string[]> = {
  'Red And Green': [ '#E5484D', '#46A758' ],
  'Blue And Yellow': [ '#0090FF', '#FFC53D' ],
  'No Color': [ '#F2F2F2', '#6E6E6E' ],
};

@Component( {
  selector: 'app-cvd-widget',
  templateUrl: './cvd-widget.component.html',
  styleUrls: [ './cvd-widget.component.scss' ],
} )
export class CvdWidgetComponent implements AfterViewInit, OnDestroy {

  /** the picture to open on ('./assets/...'); the page picks a random theme's screenshot */
  @Input() set src( value: string ) {
    if ( value && value !== this.currentSrc ) {
      this.currentSrc = value;
      this.sourceName = 'codigrate-theme';
      this.loadUrl( value );
    }
  }
  @Input() type: CvdType = 'deuteranomaly';
  @Output() stateChange = new EventEmitter<CvdState>();

  @ViewChild( 'original' ) originalRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild( 'simulated' ) simulatedRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild( 'file' ) fileRef?: ElementRef<HTMLInputElement>;

  readonly TYPES = CVD_TYPES;
  /** the conditions by group (red and green, blue and yellow, no color), two to a row in the list */
  readonly GROUPS = CVD_TYPES.reduce( ( groups, t ) => {
    const group = groups.find( g => g.name === t.group );
    group ? group.types.push( t ) : groups.push( { name: t.group, dots: GROUP_DOTS[ t.group ] || [], types: [ t ] } );
    return groups;
  }, [] as { name: string; dots: string[]; types: CvdInfo[] }[] );
  /** where the split sits, 0..100 (% of the picture's width from the left) */
  split = 50;
  dragging = false;
  dropHover = false;
  loading = false;
  error = '';
  private readonly MAX_SIDE = 1600;
  private currentSrc = '';
  private sourceName = 'image';
  private base: ImageData | null = null;
  private cache = new Map<CvdType, ImageData>();
  private viewReady = false;
  private objectUrl = '';

  constructor( private zone: NgZone ) {
  }

  get info(): CvdInfo {
    return this.TYPES.find( t => t.type === this.type ) || this.TYPES[ 0 ];
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.paint();
  }

  ngOnDestroy(): void {
    if ( this.objectUrl ) {
      URL.revokeObjectURL( this.objectUrl );
    }
  }

  pick( type: CvdType ): void {
    this.type = type;
    this.paintSimulated();
    this.emit();
  }

  // ---------------------------------------------------------------- the picture in

  openFile(): void {
    this.fileRef?.nativeElement.click();
  }

  onFile( event: Event ): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[ 0 ];
    if ( file ) {
      this.loadFile( file );
    }
    input.value = '';
  }

  onDragOver( event: DragEvent ): void {
    event.preventDefault();
    this.dropHover = true;
  }

  onDrop( event: DragEvent ): void {
    event.preventDefault();
    this.dropHover = false;
    const file = event.dataTransfer?.files && event.dataTransfer.files[ 0 ];
    if ( file ) {
      this.loadFile( file );
    }
  }

  /** a picture pasted anywhere on the page lands here */
  @HostListener( 'window:paste', [ '$event' ] )
  onPaste( event: ClipboardEvent ): void {
    const target = event.target as HTMLElement | null;
    if ( target && ( target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable ) ) {
      return;
    }
    const item = Array.from( event.clipboardData?.items || [] ).find( i => i.type.startsWith( 'image/' ) );
    const file = item?.getAsFile();
    if ( file ) {
      this.loadFile( file );
    }
  }

  private loadFile( file: File ): void {
    if ( !file.type.startsWith( 'image/' ) ) {
      this.error = 'That file is not an image';
      return;
    }
    if ( this.objectUrl ) {
      URL.revokeObjectURL( this.objectUrl );
    }
    this.objectUrl = URL.createObjectURL( file );
    this.sourceName = ( file.name || 'image' ).replace( /\.[^.]+$/, '' ).replace( /[^a-z0-9-_]+/gi, '-' ).toLowerCase() || 'image';
    this.loadUrl( this.objectUrl );
  }

  private loadUrl( url: string ): void {
    this.loading = true;
    this.error = '';
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => this.zone.run( () => {
      const scale = Math.min( 1, this.MAX_SIDE / Math.max( img.naturalWidth, img.naturalHeight ) );
      const w = Math.max( 1, Math.round( img.naturalWidth * scale ) );
      const h = Math.max( 1, Math.round( img.naturalHeight * scale ) );
      const c = document.createElement( 'canvas' );
      c.width = w;
      c.height = h;
      const ctx = c.getContext( '2d', { willReadFrequently: true } );
      if ( !ctx ) {
        this.loading = false;
        return;
      }
      ctx.drawImage( img, 0, 0, w, h );
      this.base = ctx.getImageData( 0, 0, w, h );
      this.cache.clear();
      this.loading = false;
      this.paint();
      this.emit();
    } );
    img.onerror = () => this.zone.run( () => {
      this.loading = false;
      this.error = 'This image could not be opened';
    } );
    img.src = url;
  }

  // ---------------------------------------------------------------- drawing

  private paint(): void {
    if ( !this.viewReady || !this.base ) {
      return;
    }
    const o = this.originalRef?.nativeElement;
    if ( o ) {
      o.width = this.base.width;
      o.height = this.base.height;
      o.getContext( '2d' )?.putImageData( this.base, 0, 0 );
    }
    this.paintSimulated();
  }

  private paintSimulated(): void {
    const s = this.simulatedRef?.nativeElement;
    if ( !s || !this.base ) {
      return;
    }
    s.width = this.base.width;
    s.height = this.base.height;
    s.getContext( '2d' )?.putImageData( this.simulated( this.type ), 0, 0 );
  }

  /** the picture as `type` sees it, worked out once per picture */
  simulated( type: CvdType ): ImageData {
    let out = this.cache.get( type );
    if ( !out && this.base ) {
      out = simulateCvd( this.base, type );
      this.cache.set( type, out );
    }
    return out!;
  }

  private emit(): void {
    this.stateChange.emit( { base: this.base, type: this.type, name: this.sourceName } );
  }

  // ---------------------------------------------------------------- the split

  onPointerDown( event: PointerEvent ): void {
    this.dragging = true;
    ( event.currentTarget as HTMLElement ).setPointerCapture?.( event.pointerId );
    this.moveSplit( event );
  }

  onPointerMove( event: PointerEvent ): void {
    if ( this.dragging ) {
      this.moveSplit( event );
    }
  }

  onPointerUp(): void {
    this.dragging = false;
  }

  onSplitKey( event: KeyboardEvent ): void {
    const step = event.shiftKey ? 10 : 2;
    if ( event.key === 'ArrowLeft' ) {
      this.split = Math.max( 0, this.split - step );
      event.preventDefault();
    } else if ( event.key === 'ArrowRight' ) {
      this.split = Math.min( 100, this.split + step );
      event.preventDefault();
    }
  }

  /** the split follows the pointer across the picture as drawn (object-fit: contain leaves bands around it) */
  private moveSplit( event: PointerEvent ): void {
    const canvas = this.originalRef?.nativeElement;
    if ( !canvas || !this.base ) {
      return;
    }
    const box = canvas.getBoundingClientRect();
    const scale = Math.min( box.width / this.base.width, box.height / this.base.height );
    const drawnW = this.base.width * scale;
    const left = box.left + ( box.width - drawnW ) / 2;
    this.split = Math.max( 0, Math.min( 100, ( ( event.clientX - left ) / drawnW ) * 100 ) );
  }

  /** the split in the stage's own percent, where the drawn picture is narrower than the stage */
  get splitInStage(): number {
    const canvas = this.originalRef?.nativeElement;
    if ( !canvas || !this.base || !canvas.clientWidth ) {
      return this.split;
    }
    const scale = Math.min( canvas.clientWidth / this.base.width, canvas.clientHeight / this.base.height );
    const drawnW = this.base.width * scale;
    const left = ( canvas.clientWidth - drawnW ) / 2;
    return ( ( left + drawnW * this.split / 100 ) / canvas.clientWidth ) * 100;
  }

  // ---------------------------------------------------------------- download

  download(): void {
    if ( !this.base ) {
      return;
    }
    const c = document.createElement( 'canvas' );
    c.width = this.base.width;
    c.height = this.base.height;
    c.getContext( '2d' )?.putImageData( this.simulated( this.type ), 0, 0 );
    c.toBlob( blob => {
      if ( !blob ) {
        return;
      }
      const url = URL.createObjectURL( blob );
      const a = document.createElement( 'a' );
      a.href = url;
      a.download = `${ this.sourceName }-${ this.type }.png`;
      document.body.appendChild( a );
      a.click();
      a.remove();
      setTimeout( () => URL.revokeObjectURL( url ), 4000 );
    }, 'image/png' );
  }
}
